/**
 * SASADM-0165 (saas-auth SASATH-0297) — the tenant's stored white-label domain: summary,
 * inline editor (domain once + a one-label prefix per surface), Verify DNS, remove, and the
 * DNS records the customer must create. All state of record lives in saas-auth; after every
 * write the parent re-fetches (`onChanged`) — nothing is patched locally.
 *
 * Every PUT recreates every host unverified and returns the domain to DRAFT, so saving over
 * an existing domain asks for a confirm first.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useRef, useState, type FormEvent } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError, redirectSourceIds } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import type {
  HostnameDto,
  PutWhitelabelDomainRequest,
  RoutingDeployOutcome,
  WhitelabelDomainDto,
  WhitelabelHostnameType,
  WhitelabelVerifyResponse,
} from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { RedirectTargetNotice } from '@/components/RedirectTargetNotice';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { TYPE_ORDER, fmtDate, whitelabelHostKey } from './whitelabelShared';

type CopyFunction = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

export interface WhitelabelDomainPanelProps {
  tenantId: number;
  /** `undefined` while loading, `null` when the load failed. */
  domain: WhitelabelDomainDto | null | undefined;
  /** The tenant's SYSTEM rows, for the editor's surface list. */
  systemRows: HostnameDto[];
  /** SASADM-0176 — the tenant's hostnames, to name the redirect sources of a HOSTNAME_IS_REDIRECT_TARGET. */
  hostnames?: HostnameDto[];
  /** The parent re-fetches hostnames + domain. */
  onChanged: () => void;
  /** SASADM-0166 — the header's tenant-routing deploy, offered after a fully verified run. */
  deployTenant: () => Promise<boolean>;
  tenantDeploy: { disabled: boolean; busy: boolean };
}

interface SurfaceDraft {
  /** APP:<appId> | LOGIN | AUTH | BRAND | ASSET | APP:wl:<id> */
  key: string;
  hostnameType: WhitelabelHostnameType;
  appId: number | null;
  label: string;
  include: boolean;
  prefix: string;
}

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const PREFIX_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const MAX_HOSTS = 10;
const WL_TYPES: WhitelabelHostnameType[] = ['APP', 'LOGIN', 'AUTH', 'BRAND', 'ASSET'];

function isWlType(type: string): type is WhitelabelHostnameType {
  return (WL_TYPES as string[]).includes(type);
}

function typeLabel(type: string, t: CopyFunction): string {
  return t(`hostnames.types.${type}`, type);
}

function appSurfaceLabel(name: string | null | undefined, t: CopyFunction): string {
  const typeText = typeLabel('APP', t);
  return name ? `${typeText}: ${name}` : typeText;
}

// Surfaces offered in the editor: one per distinct (type, app) of the tenant's SYSTEM rows
// (never API, never an APP row with no app), then any existing domain host that did not match.
function buildSurfaces(
  domain: WhitelabelDomainDto,
  systemRows: HostnameDto[],
  t: CopyFunction,
): SurfaceDraft[] {
  const existing = new Map(domain.hostnames.map((h) => [whitelabelHostKey(h), h]));
  const byKey = new Map<string, SurfaceDraft>();
  const seeded: SurfaceDraft[] = [];
  const matched = new Set<string>();

  for (const row of systemRows) {
    if (!isWlType(row.hostnameType)) continue;
    let key: string;
    let appId: number | null = null;
    if (row.hostnameType === 'APP') {
      if (row.appId == null) continue;
      appId = row.appId;
      key = `APP:${row.appId}`;
    } else {
      key = row.hostnameType;
    }
    if (byKey.has(key)) continue;
    const match = existing.get(key);
    if (match) matched.add(key);
    const surface: SurfaceDraft = {
      key,
      hostnameType: row.hostnameType,
      appId,
      label:
        row.hostnameType === 'APP'
          ? appSurfaceLabel(row.appName ?? row.appSlug, t)
          : typeLabel(row.hostnameType, t),
      include: match ? true : !domain.configured,
      prefix: match
        ? match.prefix
        : row.hostnameType === 'APP'
          ? (row.appSlug ?? '')
          : (row.hostname.split('.')[0] ?? ''),
    };
    byKey.set(key, surface);
    seeded.push(surface);
  }

  seeded.sort(
    (a, b) =>
      TYPE_ORDER.indexOf(a.hostnameType) - TYPE_ORDER.indexOf(b.hostnameType) ||
      a.label.localeCompare(b.label),
  );

  // SASADM-0169 — the primary app's prefix defaults to `app` (unless seeded from an existing
  // domain host). The primary app is the SYSTEM APP row with `isPrimary`, else the first APP
  // surface; other apps keep their slug.
  const primaryAppId =
    systemRows.find((r) => r.hostnameType === 'APP' && r.isPrimary && r.appId != null)?.appId ??
    seeded.find((s) => s.hostnameType === 'APP')?.appId;
  const primary = seeded.find((s) => s.hostnameType === 'APP' && s.appId === primaryAppId);
  if (primary && !matched.has(primary.key)) primary.prefix = 'app';

  for (const h of domain.hostnames) {
    const key = whitelabelHostKey(h);
    if (byKey.has(key)) continue;
    const surface: SurfaceDraft = {
      key,
      hostnameType: h.hostnameType,
      appId: h.hostnameType === 'APP' ? h.appId : null,
      label:
        h.hostnameType === 'APP'
          ? appSurfaceLabel(h.appSlug ?? t('hostnames.wl.unassigned', 'unassigned'), t)
          : typeLabel(h.hostnameType, t),
      include: true,
      prefix: h.prefix,
    };
    byKey.set(key, surface);
    seeded.push(surface);
  }
  return seeded;
}

function wlErrorText(err: ApiError, t: CopyFunction): string {
  if (err.status === 403) {
    return t('errors.insufficientPermissions', "You don't have permission for this action.");
  }
  const base = t(`errors.codes.${err.code}`, err.details.message ?? err.code);
  // saas-auth prefixes per-host failures with "Whitelabel prefix '<p>' …" — show it so the operator knows which row.
  const detail = err.details.message?.startsWith('Whitelabel prefix')
    ? ` ${err.details.message}`
    : '';
  return base + detail;
}

interface DraftErrors {
  domain: string | null;
  hosts: string | null;
  prefixes: Record<string, string>;
}

function validateDraft(domain: string, rows: SurfaceDraft[], t: CopyFunction): DraftErrors {
  const errors: DraftErrors = { domain: null, hosts: null, prefixes: {} };
  if (domain.length > 253 || !DOMAIN_RE.test(domain)) {
    errors.domain = t(
      'errors.codes.INVALID_WHITELABEL_DOMAIN',
      'Enter the domain as a bare name, like example.com.',
    );
  }
  const included = rows.filter((r) => r.include);
  if (included.length === 0) {
    errors.hosts = t('errors.codes.WHITELABEL_HOSTS_REQUIRED', 'Include at least one host.');
  } else if (included.length > MAX_HOSTS) {
    errors.hosts = t('hostnames.wl.tooMany', 'A white-label domain can have at most 10 hosts.');
  }
  const seen = new Set<string>();
  for (const r of included) {
    const prefix = r.prefix.trim().toLowerCase();
    if (prefix.length > 63 || !PREFIX_RE.test(prefix)) {
      errors.prefixes[r.key] = t(
        'errors.codes.INVALID_WHITELABEL_PREFIX',
        'Each prefix must be one label: lowercase letters, digits and inner hyphens, no dots.',
      );
    } else if (seen.has(prefix)) {
      errors.prefixes[r.key] = t(
        'errors.codes.WHITELABEL_DUPLICATE_PREFIX',
        'Two hosts use the same prefix. Give each host its own prefix.',
      );
    } else {
      seen.add(prefix);
    }
  }
  return errors;
}

type Action = 'save' | 'verify' | 'remove';

export function WhitelabelDomainPanel({
  tenantId,
  domain,
  systemRows,
  hostnames,
  onChanged,
  deployTenant,
  tenantDeploy,
}: WhitelabelDomainPanelProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [action, setAction] = useState<Action | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [verifyResult, setVerifyResult] = useState<WhitelabelVerifyResponse | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [domainDraft, setDomainDraft] = useState('');
  const [rowsDraft, setRowsDraft] = useState<SurfaceDraft[]>([]);
  const [attempted, setAttempted] = useState(false);
  // SASADM-0170 (SASATH-0387) — a save / verify / remove whose routing deploy was REJECTED /
  // FAILED. Never shown as success: the banner replaces the success snackbar and alerts.
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);
  const domainRef = useRef<HTMLInputElement | null>(null);
  const prefixRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const busy = action !== null;

  function enterEdit() {
    if (!domain) return;
    setDomainDraft(domain.domain ?? '');
    setRowsDraft(buildSurfaces(domain, systemRows, t));
    setAttempted(false);
    setError(null);
    setVerifyResult(null);
    setMode('edit');
  }

  function cancelEdit() {
    setError(null);
    setAttempted(false);
    setMode('view');
  }

  function patchRow(key: string, patch: Partial<SurfaceDraft>) {
    setRowsDraft((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  const normDomain = domainDraft.trim().toLowerCase();
  const draftErrors = attempted ? validateDraft(normDomain, rowsDraft, t) : null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || !domain) return;
    setAttempted(true);
    const errs = validateDraft(normDomain, rowsDraft, t);
    if (errs.domain) {
      domainRef.current?.focus();
      return;
    }
    const firstBad = rowsDraft.find((r) => r.include && errs.prefixes[r.key]);
    if (firstBad) {
      prefixRefs.current[firstBad.key]?.focus();
      return;
    }
    if (errs.hosts) return;
    if (domain.configured) {
      setConfirmSave(true);
      return;
    }
    void doSave();
  }

  async function doSave() {
    const body: PutWhitelabelDomainRequest = {
      domain: normDomain,
      hosts: rowsDraft
        .filter((r) => r.include)
        .map((r) => {
          const prefix = r.prefix.trim().toLowerCase();
          return r.hostnameType === 'APP' && r.appId != null
            ? { prefix, hostnameType: r.hostnameType, appId: r.appId }
            : { prefix, hostnameType: r.hostnameType };
        }),
    };
    setAction('save');
    setError(null);
    try {
      const res = await apiAdmin.whitelabelDomain.put(tenantId, body);
      setConfirmSave(false);
      setMode('view');
      setVerifyResult(null);
      if (isDeployProblem(res.routingDeploy)) {
        setDeployFailure(res.routingDeploy);
      } else {
        setDeployFailure(null);
        snack.success(
          t(
            'hostnames.wl.saved',
            'White-label domain saved. Verify DNS once the customer has created the records.',
          ),
        );
      }
      onChanged();
    } catch (err) {
      setConfirmSave(false);
      if (err instanceof ApiError) setError(err);
    } finally {
      setAction(null);
    }
  }

  async function doVerify() {
    setAction('verify');
    setError(null);
    try {
      const res = await apiAdmin.whitelabelDomain.verify(tenantId);
      // A verify whose routing deploy failed is not "verified / live": only the banner renders.
      if (isDeployProblem(res.routingDeploy)) {
        setVerifyResult(null);
        setDeployFailure(res.routingDeploy);
      } else {
        setDeployFailure(null);
        setVerifyResult(res);
      }
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err);
        if (err.status === 404) onChanged();
      }
    } finally {
      setAction(null);
    }
  }

  async function handleDeployNow() {
    const ok = await deployTenant();
    if (ok) setVerifyResult(null);
  }

  async function handleDeployFromBanner() {
    const ok = await deployTenant();
    if (ok) setDeployFailure(null);
  }

  async function doRemove() {
    setAction('remove');
    setError(null);
    try {
      const outcome = await apiAdmin.whitelabelDomain.remove(tenantId);
      setConfirmRemove(false);
      setVerifyResult(null);
      if (isDeployProblem(outcome)) {
        setDeployFailure(outcome);
      } else {
        setDeployFailure(null);
        snack.success(t('hostnames.wl.removed', 'White-label domain removed.'));
      }
      onChanged();
    } catch (err) {
      setConfirmRemove(false);
      if (err instanceof ApiError) {
        setError(err);
        if (err.status === 404) onChanged();
      }
    } finally {
      setAction(null);
    }
  }

  const spinner = <CircularProgress size={16} color="inherit" />;
  const failureBanner = deployFailure ? (
    <RoutingDeployBanner
      outcome={deployFailure}
      currentTenantId={tenantId}
      onClose={() => setDeployFailure(null)}
      action={
        <Button
          color="inherit"
          size="small"
          disabled={tenantDeploy.disabled}
          startIcon={tenantDeploy.busy ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => void handleDeployFromBanner()}
        >
          {t('routingDeploy.deployTenant', "Deploy this tenant's routing")}
        </Button>
      }
    />
  ) : null;
  // SASADM-0176 (SASATH-0391/0392) — a save / remove that would delete a redirect target.
  const byId = new Map((hostnames ?? []).map((h) => [h.id, h.hostname]));
  const banner = !error ? null : error.code === 'HOSTNAME_IS_REDIRECT_TARGET' ? (
    <RedirectTargetNotice
      err={error}
      sourceHostnames={redirectSourceIds(error).flatMap((id) => byId.get(id) ?? [])}
      onClose={() => setError(null)}
    />
  ) : (
    <ErrorBanner message={wlErrorText(error, t)} />
  );

  function renderBody() {
    if (domain === undefined) {
      return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Stack>
      );
    }
    if (domain === null) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onChanged}>
              {t('hostnames.wl.retry', 'Retry')}
            </Button>
          }
        >
          {t('hostnames.wl.loadError', "Couldn't load the white-label domain.")}
        </Alert>
      );
    }
    if (mode === 'edit') return renderEditor();
    if (!domain.configured) {
      return (
        <Box>
          {banner}
          <Typography sx={{ mb: 2 }}>
            {t(
              'hostnames.wl.none',
              'This tenant has no white-label domain. Its users reach it on the system URLs below.',
            )}
          </Typography>
          <Button variant="contained" onClick={enterEdit} disabled={busy}>
            {t('hostnames.wl.setUp', 'Set up white-label domain')}
          </Button>
        </Box>
      );
    }
    return renderConfigured(domain);
  }

  function renderConfigured(d: WhitelabelDomainDto) {
    const active = d.state === 'ACTIVE';
    const records = d.dnsRecords ?? [];
    const unverifiedHosts = verifyResult
      ? verifyResult.results.filter((r) => !r.dnsVerified).map((r) => r.hostname)
      : [];
    return (
      <Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {d.domain}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={
              active
                ? d.activatedAt
                  ? t('hostnames.wl.state.active', 'Active since {{date}}', {
                      date: fmtDate(d.activatedAt),
                    })
                  : t('hostnames.wl.state.activeNoDate', 'Active')
                : t('hostnames.wl.state.draft', 'Draft — not live until every host verifies')
            }
          />
        </Stack>
        {d.state === 'DRAFT' && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t('hostnames.wl.draftHelp', 'Hosts go live when every host verifies.')}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant={active ? 'outlined' : 'contained'}
            onClick={() => void doVerify()}
            disabled={busy}
            startIcon={action === 'verify' ? spinner : null}
          >
            {t('hostnames.wl.verify', 'Verify DNS')}
          </Button>
          <Button variant="outlined" onClick={enterEdit} disabled={busy}>
            {t('hostnames.wl.edit', 'Edit')}
          </Button>
          <Button
            color="error"
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
            startIcon={action === 'remove' ? spinner : null}
          >
            {t('hostnames.wl.remove', 'Remove white-label domain')}
          </Button>
        </Stack>
        {banner}
        {verifyResult && !verifyResult.allVerified && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'hostnames.wl.verifyPartial',
              'Not verified yet: {{hosts}}. Check the DNS records below — DNS changes can take a while to propagate — then verify again.',
              { hosts: unverifiedHosts.join(', ') },
            )}
          </Alert>
        )}
        {verifyResult?.promotedThisRun === true && (
          // SASATH-0382 — saas-auth deployed this tenant's routing itself: no deploy offer.
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Tooltip title={t('common.close', 'Close')}>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label={t('common.close', 'Close')}
                  onClick={() => setVerifyResult(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            {t(
              'hostnames.wl.liveAuto',
              'White-label domain is live. Routing was deployed automatically.',
            )}
          </Alert>
        )}
        {verifyResult?.allVerified && verifyResult.promotedThisRun !== true && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            // MUI renders `action` INSTEAD of the built-in close button, so the × is added here.
            action={
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Button
                  color="inherit"
                  size="small"
                  disabled={tenantDeploy.disabled || busy}
                  startIcon={
                    tenantDeploy.busy ? <CircularProgress size={16} color="inherit" /> : null
                  }
                  onClick={() => void handleDeployNow()}
                >
                  {t('hostnames.wl.deployNow', "Deploy this tenant's routing")}
                </Button>
                <Tooltip title={t('common.close', 'Close')}>
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label={t('common.close', 'Close')}
                    onClick={() => setVerifyResult(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          >
            {t(
              'hostnames.wl.verifiedAllDeploy',
              "All hosts verified — the domain is active. Deploy this tenant's routing to put the white-label URLs live.",
            )}
          </Alert>
        )}
        {d.dnsConfigured ? (
          records.length > 0 && (
            <Table
              size="small"
              aria-label={t('hostnames.wl.dnsTitle', 'DNS records the customer must create')}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('hostnames.wl.colHost', 'Host')}</TableCell>
                  <TableCell>{t('hostnames.wl.colType', 'Type')}</TableCell>
                  <TableCell>{t('hostnames.wl.colTarget', 'Target')}</TableCell>
                  <TableCell>{t('hostnames.wl.colStatus', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={`${r.host}|${r.target}`}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          {r.host}
                        </Typography>
                        <CopyToClipboardButton value={r.host} />
                      </Stack>
                    </TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          {r.target}
                        </Typography>
                        <CopyToClipboardButton value={r.target} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {r.dnsVerified
                        ? t('hostnames.wl.verified', 'Verified')
                        : t('hostnames.wl.notVerified', 'Not verified')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : (
          <Alert severity="info">
            {t(
              'hostnames.summary.dnsNoTarget',
              'The platform DNS target isn’t available, so instructions would be incomplete.',
            )}
          </Alert>
        )}
      </Box>
    );
  }

  function renderEditor() {
    const saving = action === 'save';
    return (
      <Box component="form" noValidate onSubmit={handleSubmit}>
        {banner}
        <TextField
          required
          label={t('hostnames.wl.domainLabel', 'Domain')}
          value={domainDraft}
          onChange={(e) => setDomainDraft(e.target.value)}
          inputRef={domainRef}
          error={Boolean(draftErrors?.domain)}
          helperText={
            draftErrors?.domain ??
            t(
              'hostnames.wl.domainHelper',
              "The customer's own domain, e.g. example.com — not the platform's or a product's domain.",
            )
          }
          sx={{ maxWidth: 360, mb: 2 }}
          fullWidth
        />
        {draftErrors?.hosts && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {draftErrors.hosts}
          </Alert>
        )}
        <Table size="small" aria-label={t('hostnames.wl.hostsTitle', 'Hosts on this domain')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('hostnames.wl.colInclude', 'Include')}</TableCell>
              <TableCell>{t('hostnames.wl.colSurface', 'Surface')}</TableCell>
              <TableCell>{t('hostnames.wl.colPrefix', 'Prefix')}</TableCell>
              <TableCell>{t('hostnames.wl.colHostname', 'Hostname')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDraft.map((r) => {
              const prefix = r.prefix.trim().toLowerCase();
              const prefixError = r.include ? draftErrors?.prefixes[r.key] : undefined;
              return (
                <TableRow key={r.key}>
                  <TableCell>
                    <Checkbox
                      checked={r.include}
                      onChange={(e) => patchRow(r.key, { include: e.target.checked })}
                      slotProps={{
                        input: {
                          'aria-label': t('hostnames.wl.includeAria', 'Include {{surface}}', {
                            surface: r.label,
                          }),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.prefix}
                      disabled={!r.include}
                      onChange={(e) => patchRow(r.key, { prefix: e.target.value })}
                      inputRef={(el: HTMLInputElement | null) => {
                        prefixRefs.current[r.key] = el;
                      }}
                      error={Boolean(prefixError)}
                      helperText={prefixError}
                      sx={{ maxWidth: 200 }}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('hostnames.wl.prefixAria', 'Prefix for {{surface}}', {
                            surface: r.label,
                          }),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {prefix && normDomain ? `${prefix}.${normDomain}` : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={busy}
            startIcon={saving ? spinner : null}
          >
            {t('common.save', 'Save')}
          </Button>
          <Button type="button" onClick={cancelEdit} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </Stack>
        <ConfirmDialog
          open={confirmSave}
          title={t('hostnames.wl.replaceTitle', 'Replace the white-label hosts?')}
          body={t(
            'hostnames.wl.replaceBody',
            'Saving re-creates every white-label host unverified and puts the domain back in Draft. Its white-label URLs stop working until DNS verifies again and routing is redeployed.',
          )}
          severity="warning"
          confirmLabel={t('hostnames.wl.replaceConfirm', 'Save and re-verify')}
          busy={saving}
          onConfirm={() => void doSave()}
          onCancel={() => setConfirmSave(false)}
        />
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h3" component="h3" sx={{ mb: 1.5 }}>
        {t('hostnames.wl.title', 'White-label domain')}
      </Typography>
      {failureBanner}
      {renderBody()}
      <ConfirmDialog
        open={confirmRemove}
        title={t('hostnames.wl.removeTitle', 'Remove white-label domain?')}
        body={t(
          'hostnames.wl.removeBody',
          'This deletes {{domain}} and its {{count}} hostnames. Customers will no longer reach this tenant on them.',
          { domain: domain?.domain ?? '', count: domain?.hostnames.length ?? 0 },
        )}
        severity="error"
        confirmLabel={t('hostnames.wl.remove', 'Remove white-label domain')}
        busy={action === 'remove'}
        onConfirm={() => void doRemove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </Paper>
  );
}
